import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DurationSparkline,
  formatMs,
} from '../components/procedures/DurationSparkline';
import {
  fetchProcedureRuns,
  fetchProcedures,
  runProcedure,
  type Procedure,
  type ProcedureRun,
} from '../api/procedures';
import './ProceduresPage.css';

/**
 * The scheduled work the runtime owns, and whether it is actually running.
 *
 * Ported from the predecessor's `Workflows` view. The runtime calls these
 * **procedures** — `/api/procedures`, `owner_module`, `cost_class`, and
 * the `procedure_health` dimension in `/api/findings` — so the page uses
 * that word. "Workflows" was the old system's name for the same thing.
 *
 * Three things this page exists to make visible, each earned:
 *
 * **A never-dispatched trigger looks exactly like a healthy idle one.**
 * A procedure with no run history has `last_run_status: null`, which reads
 * as "nothing wrong" unless the absence is called out. It is the defect
 * `/api/findings` reports as `procedure_health / never_ran`, and on
 * `wingd` it is currently true of **one** of eight — `work.pulse.deliver`,
 * measured 2026-09-26 against the running instance. An earlier draft of
 * this comment said two; that number was never measured and is retracted
 * rather than quietly softened. `/api/findings` had already answered
 * `count: 1` for the same dimension.
 *
 * **A run abandoned mid-flight stays `running` forever.** Nothing
 * reconciles a row whose process died — a container restart is the usual
 * cause — so it is indistinguishable from work in progress. See
 * `STUCK_MEDIAN_MULTIPLE` for why the threshold is relative.
 *
 * **An `llm` procedure costs money every time it fires.** Its cost class
 * is rendered differently from a mechanical one for that reason, so a
 * misconfigured interval is visible before the bill is.
 */

const REFRESH_MS = 15_000;
const MANUAL_TRIGGERS = new Set(['manual', 'ondemand']);

const STATUS_FILTERS = [
  'all',
  'running',
  'succeeded',
  'failed',
  'cancelled',
] as const;

const WINDOWS: Array<{ hours: number | null; label: string }> = [
  { hours: 1, label: '1h' },
  { hours: 6, label: '6h' },
  { hours: 24, label: '24h' },
  { hours: 24 * 7, label: '7d' },
  { hours: null, label: 'all' },
];

/**
 * How long a run may sit in `running` before it is presumed abandoned.
 *
 * Relative to the procedure's *own* finished runs rather than a fixed
 * number, because the spread between procedures is large — on the
 * predecessor the jira collector averaged 4.6s and the github collector
 * 892s, so any single cutoff is wrong for one of them. Five times the
 * median with a five-minute floor: high enough that a slow-but-live run
 * is not slandered, low enough that a dead one surfaces the same day.
 *
 * Nine such rows existed when this was first written, all
 * `pulse.collector.github`, the oldest 121 hours old — and invisible at
 * the default 24h window, so an operator had to widen it and then notice
 * the timestamps by eye.
 */
const STUCK_MEDIAN_MULTIPLE = 5;
const STUCK_FLOOR_MS = 5 * 60_000;

function relative(ms: number | null): string {
  if (ms === null) return 'never';
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function intervalLabel(seconds: number | undefined): string {
  if (!seconds || seconds <= 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = seconds / 3600;
  return h % 1 === 0 ? `${h}h` : `${h.toFixed(1)}h`;
}

/** When the next interval firing is due, or null when it cannot be known. */
function nextRunAt(p: Procedure): number | null {
  if (p.trigger_kind !== 'interval') return null;
  const seconds = (p.trigger as { seconds?: number }).seconds;
  const last = p.health?.last_run_at_ms ?? null;
  if (!seconds || last === null) return null;
  return last + seconds * 1000;
}

function countdown(targetMs: number): string {
  const diff = targetMs - Date.now();
  // Overdue is worth seeing, not hiding: an interval procedure past its
  // next firing is either mid-run or not firing at all.
  if (diff <= 0) return 'due';
  const m = Math.round(diff / 60_000);
  if (m < 60) return `in ${m}m`;
  const h = diff / 3_600_000;
  return h % 1 === 0 ? `in ${h}h` : `in ${h.toFixed(1)}h`;
}

function medianDuration(runs: ProcedureRun[], name: string): number | null {
  const durations = runs
    .filter((r) => r.procedure_name === name && r.duration_ms !== null)
    .map((r) => r.duration_ms as number)
    .sort((a, b) => a - b);
  return durations[Math.floor(durations.length / 2)] ?? null;
}

function isStuck(
  run: ProcedureRun,
  median: number | null,
  now: number,
): boolean {
  if (run.status !== 'running') return false;
  const age = now - run.started_at_ms;
  const limit = Math.max(STUCK_FLOOR_MS, (median ?? 0) * STUCK_MEDIAN_MULTIPLE);
  return age > limit;
}

export function ProceduresPage() {
  const [procedures, setProcedures] = useState<Procedure[] | null>(null);
  const [runs, setRuns] = useState<ProcedureRun[] | null>(null);
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>('all');
  const [windowHours, setWindowHours] = useState<number | null>(24);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const since =
      windowHours === null
        ? undefined
        : Date.now() - windowHours * 3_600_000;
    // `allSettled`: the catalogue and the history are separate reads and
    // one failing must not blank the other.
    const [catalogue, history] = await Promise.allSettled([
      fetchProcedures(),
      fetchProcedureRuns({
        status: status === 'all' ? undefined : status,
        since_ms: since,
        limit: 300,
      }),
    ]);
    if (catalogue.status === 'fulfilled') {
      setProcedures(catalogue.value.procedures);
    }
    if (history.status === 'fulfilled') setRuns(history.value.runs);
    const failed = [catalogue, history].find((r) => r.status === 'rejected');
    setError(
      failed && failed.status === 'rejected' ? String(failed.reason) : null,
    );
  }, [status, windowHours]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const now = Date.now();

  const durationsByName = useMemo(() => {
    const map = new Map<string, number[]>();
    // Oldest first, which is the order a sparkline reads in.
    for (const r of [...(runs ?? [])].reverse()) {
      if (r.duration_ms === null) continue;
      const bucket = map.get(r.procedure_name) ?? [];
      bucket.push(r.duration_ms);
      map.set(r.procedure_name, bucket);
    }
    return map;
  }, [runs]);

  const stuckRuns = useMemo(() => {
    const rows = runs ?? [];
    return rows.filter((r) =>
      isStuck(r, medianDuration(rows, r.procedure_name), now),
    );
  }, [runs, now]);

  async function trigger(name: string) {
    setRunning(name);
    setNotice(null);
    try {
      const run = await runProcedure(name);
      setNotice(
        `${name} ran: ${run.status}${
          run.duration_ms !== null ? ` in ${formatMs(run.duration_ms)}` : ''
        }`,
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(null);
    }
  }

  return (
    <div className="proc-page">
      <header className="proc-page__header">
        <div>
          <h1 className="proc-page__title">Procedures</h1>
          <p className="proc-page__note">
            Scheduled work the runtime owns, and whether it is running.
          </p>
        </div>
        {procedures !== null && (
          <span className="proc-dim">
            {procedures.length} registered
            {stuckRuns.length > 0 && (
              <span className="proc-page__alarm">
                {' · '}
                {stuckRuns.length} presumed abandoned
              </span>
            )}
          </span>
        )}
      </header>

      {error && <div className="proc-error">{error}</div>}
      {notice && <div className="proc-notice">{notice}</div>}

      <section className="proc-section">
        {procedures === null ? (
          <p className="proc-dim">Loading…</p>
        ) : procedures.length === 0 ? (
          <p className="proc-dim">
            No procedures are registered. A procedure is contributed by a
            module, so an instance that enables none has none.
          </p>
        ) : (
          <ul className="proc-cards">
            {procedures.map((p) => {
              const health = p.health ?? null;
              const neverRan =
                !health || health.last_run_at_ms === null;
              const next = nextRunAt(p);
              const durations = durationsByName.get(p.name) ?? [];
              const manual = MANUAL_TRIGGERS.has(p.trigger_kind);
              return (
                <li
                  key={p.name}
                  className={`proc-card${neverRan ? ' is-never-ran' : ''}${
                    health?.last_run_status === 'failed' ? ' is-failed' : ''
                  }`}
                >
                  <div className="proc-card__top">
                    <span className="proc-card__name">{p.name}</span>
                    {(() => {
                      // The runs, when there are any, over the declaration:
                      // `cost_class` defaults to mechanical, so a procedure
                      // that narrates without declaring it was badged
                      // mechanical — every procedure on wingd was, while the
                      // briefing spent tokens on each run.
                      const observed = p.cost_class_observed ?? null;
                      const shown = observed ?? p.cost_class;
                      const undeclared = observed !== null && observed !== p.cost_class;
                      const calls = health?.runs_with_model_calls ?? 0;
                      return (
                        <span
                          className={`proc-cost proc-cost--${shown}${undeclared ? ' proc-cost--undeclared' : ''}`}
                          title={
                            undeclared
                              ? `Declared ${p.cost_class}, but ${calls} of its runs called a model. ` +
                                `Its registration should declare cost_class='${observed}'.`
                              : shown === 'llm'
                                ? 'Calls a model when it fires — this one costs money on a timer'
                                : observed === null
                                  ? `Declared ${p.cost_class}; it has not run, so nothing confirms it`
                                  : `${shown} — confirmed by its run records`
                          }
                        >
                          {shown}
                          {undeclared && ' · undeclared'}
                        </span>
                      );
                    })()}
                  </div>

                  <div className="proc-card__row proc-dim">
                    <span>{p.owner_module}</span>
                    <span>·</span>
                    <span>
                      {p.trigger_kind}
                      {p.trigger_kind === 'interval' &&
                        ` ${intervalLabel((p.trigger as { seconds?: number }).seconds)}`}
                    </span>
                    {next !== null && (
                      <>
                        <span>·</span>
                        <span
                          className={
                            next - now <= 0 ? 'proc-card__due' : undefined
                          }
                        >
                          {countdown(next)}
                        </span>
                      </>
                    )}
                  </div>

                  <div className="proc-card__row">
                    {neverRan ? (
                      // Stated, not left as an empty status: a procedure
                      // registered and never dispatched is a defect, and
                      // silence reads as health.
                      <span className="proc-status proc-status--never">
                        never ran
                      </span>
                    ) : (
                      <>
                        <span
                          className={`proc-status proc-status--${health!.last_run_status ?? 'unknown'}`}
                        >
                          {health!.last_run_status ?? 'unknown'}
                        </span>
                        <span className="proc-dim">
                          {relative(health!.last_run_at_ms)}
                        </span>
                        {health!.consecutive_failures > 0 && (
                          <span className="proc-card__fails">
                            {health!.consecutive_failures} in a row
                          </span>
                        )}
                      </>
                    )}
                    <span className="proc-card__spark">
                      <DurationSparkline durations={durations} />
                    </span>
                  </div>

                  {manual && (
                    <button
                      type="button"
                      className="proc-run"
                      onClick={() => void trigger(p.name)}
                      disabled={running === p.name}
                    >
                      {running === p.name ? 'running…' : 'Run now'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="proc-section">
        <div className="proc-section__head">
          <h2 className="proc-section__title">Runs</h2>
          <div className="proc-controls">
            <div className="proc-chips" role="group" aria-label="Status">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`proc-chip${status === s ? ' is-active' : ''}`}
                  onClick={() => setStatus(s)}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="proc-chips" role="group" aria-label="Window">
              {WINDOWS.map((w) => (
                <button
                  key={w.label}
                  type="button"
                  className={`proc-chip${windowHours === w.hours ? ' is-active' : ''}`}
                  onClick={() => setWindowHours(w.hours)}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {runs === null ? (
          <p className="proc-dim">Loading…</p>
        ) : runs.length === 0 ? (
          <p className="proc-dim">
            No runs in this window.
            {windowHours !== null &&
              ' A procedure on a long interval may simply not have fired yet — widen the window before concluding it is stuck.'}
          </p>
        ) : (
          <div className="proc-table-scroll">
            <table className="proc-table">
              <thead>
                <tr>
                  <th>procedure</th>
                  <th>status</th>
                  <th>started</th>
                  <th>duration</th>
                  <th>result</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const stuck = isStuck(
                    r,
                    medianDuration(runs, r.procedure_name),
                    now,
                  );
                  return (
                    <tr key={r.id} className={stuck ? 'is-stuck' : undefined}>
                      <td className="proc-table__name">{r.procedure_name}</td>
                      <td>
                        <span
                          className={`proc-status proc-status--${stuck ? 'abandoned' : r.status}`}
                          title={
                            stuck
                              ? `Still 'running' after ${formatMs(now - r.started_at_ms)}, well past this procedure's median — presumed abandoned rather than in progress`
                              : undefined
                          }
                        >
                          {stuck ? 'abandoned?' : r.status}
                        </span>
                      </td>
                      <td className="proc-dim">{relative(r.started_at_ms)}</td>
                      <td className="proc-table__dur">
                        {r.duration_ms !== null
                          ? formatMs(r.duration_ms)
                          : stuck
                            ? formatMs(now - r.started_at_ms)
                            : '—'}
                      </td>
                      <td className="proc-table__payload">
                        {r.error ? (
                          <span className="proc-table__err" title={r.error}>
                            {r.error}
                          </span>
                        ) : r.payload && Object.keys(r.payload).length > 0 ? (
                          <code title={JSON.stringify(r.payload, null, 2)}>
                            {JSON.stringify(r.payload)}
                          </code>
                        ) : (
                          <span className="proc-dim">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
